import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkedAuth, setCheckedAuth] = useState(false);

  useEffect(() => {
    if (!checkedAuth) {
      checkAuth();
    }
  }, [checkedAuth]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/session`, {
        withCredentials: true,
        validateStatus: (status) => status === 200 || status === 401
      });
      if (response.status === 200 && response.data.user) {
        setUser(response.data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      // Silently handle auth check errors
      setUser(null);
    } finally {
      setLoading(false);
      setCheckedAuth(true);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    if (response.data.user) {
      setUser(response.data.user);
      if (response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
      }
    }
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(
      `${API}/auth/register`,
      { name, email, password },
      { withCredentials: true }
    );
    if (response.data.user) {
      setUser(response.data.user);
      if (response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
      }
    }
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    localStorage.removeItem('auth_token');
    
    // NOVO: Limpar dados do chat ao fazer logout
    sessionStorage.removeItem('chat_currentConversationId');
    sessionStorage.removeItem('chat_messages');
    sessionStorage.clear(); // Limpa tudo por segurança
  };

  const updateProfile = async (data) => {
    const response = await axios.put(
      `${API}/auth/profile`,
      data,
      { withCredentials: true }
    );
    if (response.data.user) {
      setUser(response.data.user);
    }
    return response.data;
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/session`, { withCredentials: true });
      if (response.data.user) {
        setUser(response.data.user);
        console.log('✅ User refreshed from backend');
        return response.data.user;
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
      return null;
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    refreshUser,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
